import { MonoLabel } from '@/design-system';
import type { ReportRecipient } from '@/entities/report';

import { reportCopy } from '../copy';

export interface RecipientListProps {
    recipients: readonly ReportRecipient[];
    /** De inkt van de kaart eromheen: amber in de bevestiging van goedkeuren. */
    tone?: 'warn' | 'neutral';
}

/**
 * "Gaat naar": wie het rapport krijgt, met rol en e-mailadres, en wie niets krijgt
 * omdat er geen e-mailadres is. Zonder ontvanger met een e-mailadres staat er de weg
 * naar "Klant bewerken".
 */
export function RecipientList({ recipients, tone = 'neutral' }: RecipientListProps) {
    const deliverable = recipients.filter((recipient) => recipient.email !== null);
    const skipped = recipients.filter((recipient) => recipient.email === null);
    const ink = tone === 'warn' ? 'text-[var(--warn-ink)]' : 'text-[var(--text-primary)]';

    return (
        <>
            <MonoLabel
                size={10}
                tone={tone === 'warn' ? 'warn' : 'muted'}
                as="div"
                className="mt-[14px] mb-2"
            >
                {reportCopy.confirmRecipients}
            </MonoLabel>
            {deliverable.length === 0 ? (
                <p className={`m-0 text-[13px] leading-[1.5] ${ink}`}>
                    {reportCopy.confirmNoRecipients}
                </p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {deliverable.map((recipient) => (
                        <li
                            key={`${recipient.roleKey}:${recipient.email ?? recipient.name}`}
                            className={`text-[13px] leading-[1.6] ${ink}`}
                        >
                            {[recipient.name, recipient.role, recipient.email]
                                .filter(Boolean)
                                .join(' · ')}
                        </li>
                    ))}
                </ul>
            )}
            {skipped.length > 0 ? (
                <ul className="m-0 mt-1 list-none p-0">
                    {skipped.map((recipient) => (
                        <li
                            key={`${recipient.roleKey}:${recipient.name}`}
                            className={`text-[13px] leading-[1.6] ${ink}`}
                        >
                            {reportCopy.confirmWithoutEmail(recipient.name)}
                        </li>
                    ))}
                </ul>
            ) : null}
        </>
    );
}
